import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusCircle, Pencil, CheckCircle2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useActiveOrganization } from '@/features/organizations/hooks/useActiveOrganization';
import { MemberRole } from '@/core/constants/roles';
import { useDashboard } from '../hooks/useDashboard';
import {
  useDeleteWidget,
  useUpdateWidgetLayouts,
  toLayoutMutations,
} from '../hooks/useDashboardMutations';
import { DashboardGrid } from './DashboardGrid/DashboardGrid';
import { AddWidgetModal } from './AddWidgetModal/AddWidgetModal';
import type { WidgetDef, WidgetScope, WidgetLayoutUpdate } from '../types';

export function BoardDashboardView() {
  const { t } = useTranslation('dashboard');
  const { orgId, projectId, boardId } = useParams<{
    orgId: string;
    projectId: string;
    boardId: string;
  }>();
  const storeOrgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const tenantId = orgId ?? storeOrgId;

  const { activeOrganization } = useActiveOrganization();
  const isAdmin =
    activeOrganization?.role === MemberRole.Owner || activeOrganization?.role === MemberRole.Admin;

  const scope: WidgetScope = {
    type: 'board',
    tenantId,
    projectId: projectId ?? '',
    boardId: boardId ?? '',
  };

  const [isEditing, setIsEditing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editWidget, setEditWidget] = useState<WidgetDef | null>(null);

  const { data: shell, isLoading } = useDashboard(scope);
  const deleteMutation = useDeleteWidget(scope);
  const layoutMutation = useUpdateWidgetLayouts(scope);

  const handleDelete = (widgetId: string) => {
    deleteMutation.mutate(widgetId);
  };

  const handlePersistLayout = (updates: WidgetLayoutUpdate[]) => {
    const mutations = toLayoutMutations(updates, shell?.widgets ?? []);
    if (mutations.length > 0) layoutMutation.mutate(mutations);
  };

  const handleEditWidget = (widget: WidgetDef) => {
    setEditWidget(widget);
    setModalOpen(true);
  };

  const handleAddWidget = () => {
    setEditWidget(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditWidget(null);
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6" data-testid="board-dashboard-view">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">{t('title')}</h1>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {(shell?.widgets.length ?? 0) > 0 && (
              <>
                {isEditing ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    data-testid="dashboard-done-btn"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t('editMode.disable')}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    data-testid="dashboard-edit-btn"
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    {t('editMode.enable')}
                  </Button>
                )}
              </>
            )}
            <Button size="sm" onClick={handleAddWidget} data-testid="dashboard-add-widget-btn">
              <PlusCircle className="mr-2 h-4 w-4" />
              {t('actions.addWidget')}
            </Button>
          </div>
        )}
      </div>

      <DashboardGrid
        widgets={shell?.widgets ?? []}
        scope={scope}
        isEditing={isEditing}
        isAdmin={isAdmin}
        isLoading={isLoading}
        onEditWidget={handleEditWidget}
        onDeleteWidget={handleDelete}
        onPersistLayout={handlePersistLayout}
      />

      <AddWidgetModal
        open={modalOpen}
        scope={scope}
        editWidget={editWidget}
        existingWidgets={shell?.widgets ?? []}
        onClose={handleModalClose}
      />
    </div>
  );
}
